export type AuthMode = 'stub' | 'entra'

export type AppEnv = 'prod' | 'dev' | 'local'

export type AppConfig = {
  apiBaseUrl: string
  appEnv: AppEnv
  auth:
    | { kind: 'stub' }
    | {
        kind: 'entra'
        tenantId: string
        spaClientId: string
        apiScope: string
      }
}

type RawEnv = Record<string, string | undefined>

const isAuthMode = (value: string | undefined): value is AuthMode =>
  value === 'stub' || value === 'entra'

const isAppEnv = (value: string | undefined): value is AppEnv =>
  value === 'prod' || value === 'dev' || value === 'local'

/** Browser-tab title, suffixed outside prod so environments are
 * distinguishable across open tabs. */
export const documentTitle = (appEnv: AppEnv): string =>
  appEnv === 'prod' ? 'OTilie' : `OTilie - ${appEnv}`

const requireKey = (env: RawEnv, key: string, missing: string[]): string => {
  const value = env[key]
  if (value === undefined || value === '') {
    missing.push(key)
    return ''
  }
  return value
}

export function loadConfig(env: RawEnv): AppConfig {
  const missing: string[] = []
  const apiBaseUrl = requireKey(env, 'VITE_API_BASE_URL', missing)
  const rawAppEnv = env['VITE_APP_ENV']
  if (!isAppEnv(rawAppEnv)) {
    missing.push('VITE_APP_ENV (must be "prod", "dev" or "local")')
  }
  const appEnv: AppEnv = isAppEnv(rawAppEnv) ? rawAppEnv : 'prod'
  const rawMode = env['VITE_AUTH_MODE']
  if (!isAuthMode(rawMode)) {
    missing.push('VITE_AUTH_MODE (must be "stub" or "entra")')
  }

  if (rawMode === 'entra') {
    const tenantId = requireKey(env, 'VITE_ENTRA_TENANT_ID', missing)
    const spaClientId = requireKey(env, 'VITE_ENTRA_SPA_CLIENT_ID', missing)
    const apiScope = requireKey(env, 'VITE_API_SCOPE', missing)
    if (missing.length > 0) {
      throw new ConfigError(missing)
    }
    return {
      apiBaseUrl,
      appEnv,
      auth: { kind: 'entra', tenantId, spaClientId, apiScope },
    }
  }

  if (missing.length > 0) {
    throw new ConfigError(missing)
  }
  return { apiBaseUrl, appEnv, auth: { kind: 'stub' } }
}

export class ConfigError extends Error {
  readonly missing: readonly string[]
  constructor(missing: string[]) {
    super(
      `Missing or invalid frontend env vars: ${missing.join(', ')}. ` +
        `Set them in frontend/.env.local (dev) or in the deploy environment.`,
    )
    this.name = 'ConfigError'
    this.missing = missing
  }
}
